import { useState, useEffect, useCallback } from 'react';
import { Search, UserCog, X, Building2, User as UserIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { authAPI, ImpersonatableCompany, ImpersonatableUser } from '../../lib/api';
import { Alert, AlertDescription } from '../ui/alert';

interface LoginAsProps {
  onImpersonationStart?: (targetUser: ImpersonatableUser, targetToken: string, originalToken: string) => void;
  isHelixAdmin?: boolean;
  isSupplierAdmin?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export default function LoginAs({
  onImpersonationStart,
  isHelixAdmin = false,
  isSupplierAdmin = false,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  trigger,
}: LoginAsProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [companies, setCompanies] = useState<ImpersonatableCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<ImpersonatableCompany[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyType, setSelectedCompanyType] = useState<'all' | 'supplier' | 'customer'>('all');

  // Use external state if provided, otherwise use internal
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  useEffect(() => {
    if (open && (isHelixAdmin || isSupplierAdmin)) {
      fetchImpersonatableUsers();
    }
  }, [open, isHelixAdmin, isSupplierAdmin]);

  const filterCompanies = useCallback(() => {
    let filtered = companies;

    if (selectedCompanyType !== 'all') {
      filtered = filtered.filter((c) => c.company_type === selectedCompanyType);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((company) => {
        const companyMatch = company.company_name.toLowerCase().includes(search);
        const userMatch = company.users.some(
          (user) => user.full_name.toLowerCase().includes(search) || user.email.toLowerCase().includes(search)
        );
        return companyMatch || userMatch;
      });
    }

    setFilteredCompanies(filtered);
  }, [companies, selectedCompanyType, searchTerm]);

  useEffect(() => {
    filterCompanies();
  }, [filterCompanies]);

  const fetchImpersonatableUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getImpersonatableUsers();
      setCompanies(response.data.companies || []);
    } catch (err: unknown) {
      console.error('Error fetching impersonatable users:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (user: ImpersonatableUser) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.impersonate(user.user_id);

      // API returns standardized format: { status, message, data: { target_user, target_token, original_token } }
      const impersonationData = response.data.data;

      if (onImpersonationStart) {
        onImpersonationStart(
          impersonationData.target_user,
          impersonationData.target_token,
          impersonationData.original_token
        );
      }

      setOpen(false);
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to impersonate user');
    } finally {
      setLoading(false);
    }
  };

  if (!isHelixAdmin && !isSupplierAdmin) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2">
            <UserCog className="h-4 w-4" />
            Login As
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Login As User
          </DialogTitle>
          <DialogDescription>
            Select a customer or supplier user to impersonate. You can return to your account anytime.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies or users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                autoComplete="off"
              />
            </div>
            <div className="flex gap-1">
              <Button
                variant={selectedCompanyType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCompanyType('all')}
              >
                All
              </Button>
              <Button
                variant={selectedCompanyType === 'supplier' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCompanyType('supplier')}
              >
                Suppliers
              </Button>
              <Button
                variant={selectedCompanyType === 'customer' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCompanyType('customer')}
              >
                Customers
              </Button>
            </div>
          </div>

          {/* Company and User List */}
          <div className="overflow-y-auto max-h-[500px] space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading users...
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No users found
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <Card key={company.company_id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Building2 className="h-4 w-4" />
                        {company.company_name}
                      </CardTitle>
                      <Badge
                        variant={company.company_type === 'supplier' ? 'default' : 'secondary'}
                      >
                        {company.company_type}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {company.users.map((user) => (
                        <div
                          key={user.user_id}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <UserIcon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">{user.full_name}</div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                              {user.job_title && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {user.job_title}
                                  {user.role && ` · ${user.role}`}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!user.is_active && (
                              <Badge variant="outline" className="text-xs">
                                Inactive
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleImpersonate(user)}
                              disabled={!user.is_active || loading}
                            >
                              Login As
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Impersonation Banner Component
interface ImpersonationBannerProps {
  originalUser: { full_name: string; email: string };
  targetUser: { full_name: string; email: string };
  onStopImpersonation: () => void;
}

export function ImpersonationBanner({
  originalUser,
  targetUser,
  onStopImpersonation,
}: ImpersonationBannerProps) {
  return (
    <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between shadow-md">
      <div className="flex items-center gap-3">
        <UserCog className="h-5 w-5" />
        <div className="text-sm">
          <strong>Viewing as:</strong> {targetUser.full_name}
          <span className="mx-2">•</span>
          <span className="opacity-80">Logged in as: {originalUser.full_name}</span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onStopImpersonation}
        className="gap-2 hover:bg-amber-600 text-amber-950"
      >
        <X className="h-4 w-4" />
        Return to My Account
      </Button>
    </div>
  );
}
